import CheckmarkIcon from '@assets/icons/checkmark-thin.svg';
import i18n from '@/utils/i18n';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Animated, View } from 'react-native';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useCurrentDownloadPercentage } from '@/contexts/offline-download-management-context';
import { useEffect, useRef } from 'react';

interface Props {
  cancelDownload: () => void;
  resetDownloadProgress: () => void;
}

export const OfflineDownloadHeader: React.FC<Props> = ({
  cancelDownload,
  resetDownloadProgress,
}) => {
  const [styles, theme] = useStyles();
  const downloadPercentage = useCurrentDownloadPercentage();
  const offlineDownloadCompleted = downloadPercentage === 100;

  const heightScaleAnim = useRef(new Animated.Value(1)).current;
  const opacityAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (offlineDownloadCompleted) {
      const timer = setTimeout(() => {
        Animated.parallel([
          Animated.timing(heightScaleAnim, {
            duration: 500,
            toValue: 0,
            useNativeDriver: false,
          }),
          Animated.timing(opacityAnim, {
            duration: 500,
            toValue: 0,
            useNativeDriver: true,
          }),
        ]).start();
        // Start after 4.5s with 0.5s animation duration to align with the context timeout at 5s
      }, 4500);

      return () => clearTimeout(timer);
    }
  }, [offlineDownloadCompleted, heightScaleAnim, opacityAnim]);

  return (
    <Animated.View
      style={[
        styles.base,
        {
          opacity: opacityAnim,
          transform: [{ scaleY: heightScaleAnim }],
        },
      ]}
    >
      <View style={styles.content}>
        {offlineDownloadCompleted ? (
          <CheckmarkIcon
            height={20}
            stroke={theme.sk.color.icon.primary}
            width={20}
          />
        ) : (
          <ActivityIndicator />
        )}
        <Typography variant="paragraph-md">
          {offlineDownloadCompleted
            ? i18n.t('offline_data_synced')
            : i18n.t('syncing_offline_data', {
                percentage: downloadPercentage,
              })}
        </Typography>
      </View>
      <Button
        onPress={
          offlineDownloadCompleted ? resetDownloadProgress : cancelDownload
        }
        textStyle={styles.offlineButtonText}
        trackingData={{
          name: offlineDownloadCompleted
            ? 'location_picker.dismiss_sync_button'
            : 'location_picker.stop_sync_button',
        }}
        type="tertiary"
      >
        {i18n.t(offlineDownloadCompleted ? 'dismiss' : 'stop_sync')}
      </Button>
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.highlight.tertiary,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: 16,
  },
  content: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  offlineButtonText: {
    fontSize: 14,
  },
}));
